import { useState } from "react";
import { motion } from "framer-motion";
import { PaperPlaneTilt, Phone, Envelope, CheckCircle, SpinnerGap, WhatsappLogo } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    accommodation_type: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", phone: "", email: "", accommodation_type: "", message: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  return (
    <section
      id="iletisim"
      data-testid="contact-section"
      className="section-padding bg-[#EFEAE2]"
    >
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="overline mb-4 inline-block">İletişim</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#1A1A1A] mb-6">
              Hemen <span className="text-[#C46235]">Bilgi Alın</span>
            </h2>
            <p className="font-['Outfit'] text-base sm:text-lg text-[#4A4A4A] leading-relaxed mb-10">
              DVT Rezidans'ın sunduğu konforu keşfetmek ve detaylı bilgi almak 
              için hemen bizimle iletişime geçin.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4 group">
                <div className="w-14 h-14 rounded-2xl bg-[#4A5D23] flex items-center justify-center flex-shrink-0">
                  <Phone size={24} weight="fill" className="text-white" />
                </div>
                <div>
                  <div className="font-['Outfit'] text-sm text-[#4A4A4A]">Telefon</div>
                  <a href="tel:+904825022483" data-testid="contact-phone-link-1" className="font-['Outfit'] text-base sm:text-lg font-semibold text-[#1A1A1A] hover:text-[#4A5D23] transition-colors block">
                    0482 502 24 83
                  </a>
                  <a href="tel:+904825026111" data-testid="contact-phone-link-2" className="font-['Outfit'] text-base sm:text-lg font-semibold text-[#1A1A1A] hover:text-[#4A5D23] transition-colors block">
                    0482 502 61 11
                  </a>
                </div>
              </div>
              <a
                href="https://wa.me/905302068721?text=Merhaba%2C%20DVT%20Rezidans%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#25D366] flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                  <WhatsappLogo size={24} weight="fill" className="text-white" />
                </div>
                <div>
                  <div className="font-['Outfit'] text-sm text-[#4A4A4A]">WhatsApp</div>
                  <div className="font-['Outfit'] text-lg font-semibold text-[#1A1A1A]">
                    +90 530 206 87 21
                  </div>
                </div>
              </a>
              <div className="flex items-start gap-4 group">
                <div className="w-14 h-14 rounded-2xl bg-[#C46235] flex items-center justify-center flex-shrink-0">
                  <Envelope size={24} weight="fill" className="text-white" />
                </div>
                <div>
                  <div className="font-['Outfit'] text-sm text-[#4A4A4A]">E-posta</div>
                  <a href="mailto:rezervasyon@dvtrezidans.com" data-testid="contact-email-link-1" className="font-['Outfit'] text-base sm:text-lg font-semibold text-[#1A1A1A] hover:text-[#C46235] transition-colors block">
                    rezervasyon@dvtrezidans.com
                  </a>
                  <a href="mailto:satis@dvtrezidans.com" data-testid="contact-email-link-2" className="font-['Outfit'] text-base sm:text-lg font-semibold text-[#1A1A1A] hover:text-[#C46235] transition-colors block">
                    satis@dvtrezidans.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="card-surface p-8 sm:p-10 rounded-2xl space-y-5"
            >
              <div>
                <label className="font-['Outfit'] text-sm font-medium text-[#1A1A1A] block mb-2">
                  Ad Soyad *
                </label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Adınız Soyadınız"
                  className="w-full px-4 py-3.5 rounded-xl bg-[#EFEAE2] border border-[#D1C9B8]/50 font-['Outfit'] text-sm text-[#1A1A1A] placeholder:text-[#4A4A4A]/50 focus:outline-none focus:border-[#4A5D23] focus:ring-1 focus:ring-[#4A5D23]/30 transition-all"
                />
              </div>
              <div>
                <label className="font-['Outfit'] text-sm font-medium text-[#1A1A1A] block mb-2">
                  Telefon *
                </label>
                <input
                  data-testid="contact-input-phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  placeholder="05XX XXX XX XX"
                  className="w-full px-4 py-3.5 rounded-xl bg-[#EFEAE2] border border-[#D1C9B8]/50 font-['Outfit'] text-sm text-[#1A1A1A] placeholder:text-[#4A4A4A]/50 focus:outline-none focus:border-[#4A5D23] focus:ring-1 focus:ring-[#4A5D23]/30 transition-all"
                />
              </div>
              <div>
                <label className="font-['Outfit'] text-sm font-medium text-[#1A1A1A] block mb-2">
                  E-posta
                </label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="ornek@email.com"
                  className="w-full px-4 py-3.5 rounded-xl bg-[#EFEAE2] border border-[#D1C9B8]/50 font-['Outfit'] text-sm text-[#1A1A1A] placeholder:text-[#4A4A4A]/50 focus:outline-none focus:border-[#4A5D23] focus:ring-1 focus:ring-[#4A5D23]/30 transition-all"
                />
              </div>
              <div>
                <label className="font-['Outfit'] text-sm font-medium text-[#1A1A1A] block mb-2">
                  Konaklama Türü
                </label>
                <select
                  data-testid="contact-select-type"
                  name="accommodation_type"
                  value={form.accommodation_type}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 rounded-xl bg-[#EFEAE2] border border-[#D1C9B8]/50 font-['Outfit'] text-sm text-[#1A1A1A] focus:outline-none focus:border-[#4A5D23] focus:ring-1 focus:ring-[#4A5D23]/30 transition-all appearance-none"
                >
                  <option value="">Seçiniz</option>
                  <option value="gunluk">Günlük</option>
                  <option value="haftalik">Haftalık</option>
                  <option value="aylik">Aylık</option>
                  <option value="yillik">Yıllık</option>
                </select>
              </div>
              <div>
                <label className="font-['Outfit'] text-sm font-medium text-[#1A1A1A] block mb-2">
                  Mesajınız
                </label>
                <textarea
                  data-testid="contact-input-message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Sorularınız veya özel istekleriniz..."
                  className="w-full px-4 py-3.5 rounded-xl bg-[#EFEAE2] border border-[#D1C9B8]/50 font-['Outfit'] text-sm text-[#1A1A1A] placeholder:text-[#4A4A4A]/50 focus:outline-none focus:border-[#4A5D23] focus:ring-1 focus:ring-[#4A5D23]/30 transition-all resize-none"
                />
              </div>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={status === "loading" || status === "success"}
                className={`w-full py-4 rounded-full font-['Outfit'] font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 ${
                  status === "success"
                    ? "bg-[#4A5D23] text-white"
                    : "bg-[#C46235] text-white hover:bg-[#A6522B] hover:shadow-lg hover:shadow-[#C46235]/20"
                } disabled:opacity-70`}
              >
                {status === "loading" && <SpinnerGap size={20} className="animate-spin" />}
                {status === "success" && <CheckCircle size={20} weight="fill" />}
                {status === "idle" && <PaperPlaneTilt size={20} weight="fill" />}
                {status === "error" && <PaperPlaneTilt size={20} weight="fill" />}
                {status === "loading" && "Gönderiliyor..."}
                {status === "success" && "Mesajınız Alındı!"}
                {status === "idle" && "Gönder"}
                {status === "error" && "Hata! Tekrar Deneyin"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
